import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import { PayrunExtended } from '@xemplo/gp-types';
import { useUpdatePayrunMilestones } from '@xemplo/payrun-query';
import { safeCallAsync } from '@xemplo/safe-call';
import { ToastType, useToast } from '@xemplo/toast';

import { toFormValues, toPayload } from '../edit-payrun-milestones.helpers';
import { MilestoneFormValues, ModalDialogRef } from '../edit-payrun-milestones.types';
import { createMilestoneResolver } from '../validations/milestone-validation';

export interface UseEditMilestonesFormProps {
  payrunId: string;
  payrun: PayrunExtended;
  modalDialog: ModalDialogRef;
  onSuccess?: () => void;
}

export function useEditMilestonesForm({
  payrunId,
  payrun,
  modalDialog,
  onSuccess,
}: UseEditMilestonesFormProps) {
  const { addToast } = useToast();
  const { mutateAsync: updatePayrunDates } = useUpdatePayrunMilestones();

  const methods = useForm<MilestoneFormValues>({
    defaultValues: toFormValues(payrun),
    resolver: createMilestoneResolver(payrun.payrollModelId),
  });

  const handleCancel = useCallback(() => {
    methods.reset();
    modalDialog?.closeDialog?.();
  }, [methods, modalDialog]);

  const handleSubmit = methods.handleSubmit(async (data: MilestoneFormValues) => {
    const payload = toPayload(payrunId, data, payrun.payrollModelId);
    const [, error] = await safeCallAsync(updatePayrunDates(payload));
    if (error) {
      addToast({ text: 'Failed to update milestones', type: ToastType.Alert });
      return;
    }

    methods.reset();
    modalDialog?.closeDialog?.();
    addToast({ text: 'Milestones updated successfully', type: ToastType.Confirmation });
    onSuccess?.();
  });

  return { methods, handleCancel, handleSubmit };
}
